import React, {useCallback, useMemo, useState} from "react";
import "./index.scss";
import {connect} from "react-redux";
import {object, oneOf} from "prop-types";
import {__} from "services/i18n";
import {withToastManager} from "react-toast-notifications";
import {favoriteThreadErrorSelector} from "store/reducer/threads/favorite/selectors";
import {favoriteThreadStart} from "store/reducer/threads/favorite/reducer";
import {FavoriteButton} from "features/components/favorite-btn";
import {CategoryTagBox} from "features/components/category-tag-box";
import Title from "features/components/title";
import Text from "features/components/text";
import logoImage from "images/logo/marchio.svg";
import moment, {ISO_8601} from "moment";
import {Icon} from "features/components/icon";
import {historyObject} from "historyObject";
import {withTranslation} from "react-i18next";
import {THREAD_TYPES} from "constants.js";

/* istanbul ignore next */
const mapStateToProps = state => ({
    error: favoriteThreadErrorSelector(state)
});
/* istanbul ignore next */
const mapDispatchToProps = {
    favoriteStart: favoriteThreadStart
};

export const ThreadCard = (props) => {
    const {item, type = "default", error, color = "default", t} = props;
    const {favoriteStart} = props;
    const [favorite, setFavorite] = useState(item.favorite);

    const toggleFavorite = useCallback((event) => {
        setFavorite(!favorite);
        favoriteStart({id: item.id, favorite: !favorite});
        event.preventDefault();
        event.stopPropagation();
    }, [favorite, item, favoriteStart]);

    React.useEffect(() => {
        setFavorite(item.favorite);
    }, [item]);

    const {toastManager} = props;
    React.useEffect(() => {
        if(!error.inError) return;
        toastManager.add(__(error.error), { appearance: 'error', id: 'favorite-video-fail' })
    }, [error, toastManager]);

    const renderUser = useMemo(() => {
        return <div className={"user"}>
            <img src={item.avatar ?? logoImage} alt={"user"} />
            <div>
                <Text size={12}>{__("common.opened-at-by", [{key:"[date]", value: moment(item.creation_date, ISO_8601).format("DD MMM")}])}</Text>
                <Title size={16}>{item.user_name}</Title>
            </div>
        </div>
    }, [item])
    const favoriteRender = useMemo(() => {
        return <div className={"favorite-section"}>
            {type === "small" && renderUser}
            <FavoriteButton favorite={favorite} toggleFavorite={toggleFavorite} />
        </div>
    }, [toggleFavorite, favorite, type, renderUser])

    const contentRender = useMemo(() => {
        let help = [];
        if(item.type === THREAD_TYPES.request) help.push(t("common.thread.request"));
        return <div className={`content ${item.type}`}>
            <CategoryTagBox trim={true} categories={[...help, ...item.categories]} tags={[]} />
            <Title size={20}>{item.title}</Title>
            <Text size={16}>{item.message}</Text>
            {type !== "small" && renderUser}
        </div>
    }, [item, t, type, renderUser]);

    const bestAnswerRender = useMemo(() => {
        if(!item.is_archived || !item.best_answer || type === "small") {
            return null;
        }
        let comment = item.best_answer.comment;
        item.best_answer.tags.forEach(tag => {
            comment = comment.replace("$TAG", `${tag.key}`);
        });
        return <div className="single-best-answer">
            <div className={"data"}>
                {!!item.best_answer.user.avatar ? <img src={item.best_answer.user.avatar} alt="author avatar"/> : <div className="default"><Icon>account-circle</Icon></div>}
                <div className={"texts"}>
                    <div className={"best"}>
                        <Icon>diamond</Icon>
                        <Text size={12}>{__("common.thread.best_answer")}</Text>
                    </div>
                    <Title size={16}>{item.best_answer.user.name}</Title>
                </div>
            </div>
            <Text size={14}>{comment}</Text>
        </div>
    }, [item, type]);

    const answerRender = useMemo(() => {
        return <div className={"answers"}>
            <div className={"data"}>
                <Icon>comment-vs</Icon>
                <Text size={14}>{item.number_of_responses} {__(`common.response.${item.number_of_responses === 1 ? "single":"plural"}`)}</Text>
                {item.number_of_responses > 0 && <Text size={10}>({__("common.last")} {moment(item.last_answer, ISO_8601).calendar()})</Text>}
            </div>
            <Icon>chevron-right</Icon>
        </div>
    }, [item]);

    const navigateToDetail = useCallback(() => {
        historyObject.push(`/threads/${item.id}`);
    }, [item.id]);

    return <div className={`thread-card ${type} ${color}`} onClick={navigateToDetail}>
        {favoriteRender}
        {contentRender}
        {bestAnswerRender}
        {answerRender}
    </div>
};

ThreadCard.propTypes = {
    item: object.isRequired,
    type: oneOf(["small", "default"])
}

export default connect(mapStateToProps, mapDispatchToProps)(withToastManager(withTranslation()(ThreadCard)));